import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { getLatestGuardianAlphaRestartResult } from '@/api/guardianAlpha'
import JobStatusBadge from '@/components/JobStatusBadge'
import type {
  CommandStatus,
  GuardianAlphaRestartResult,
  GuardianAlphaRestartResultTarget,
} from '@/types'
import { formatKstDateTime } from '@/utils/datetime'
import { nodeDetailPath } from '@/utils/navigation'
import styles from './GuardianAlphaRestartResultPage.module.css'

const ACTIVE_STATUSES = new Set<CommandStatus>([
  'queued',
  'dispatched',
  'accepted',
  'running',
])
const POLL_INTERVAL_MS = 3000

interface ResultViewState {
  nodeId: string
  result: GuardianAlphaRestartResult | null
  loading: boolean
  error: string | null
}

function errorMessage(caught: unknown): string {
  if (
    typeof caught === 'object'
    && caught !== null
    && 'message' in caught
    && typeof caught.message === 'string'
  ) {
    return caught.message
  }
  return '최근 재시작 결과를 불러오지 못했습니다.'
}

function RestartLog({ target }: { target: GuardianAlphaRestartResultTarget }) {
  if (target.restart_log === null && target.log_truncated) {
    return (
      <p className={styles.logNotice}>
        전체 저장 상한으로 로그가 생략됐습니다
      </p>
    )
  }

  if (target.restart_log === null) {
    return <p className={styles.emptyText}>로그를 수집하지 못했습니다</p>
  }

  if (target.restart_log === '') {
    return <p className={styles.emptyText}>출력된 로그가 없습니다</p>
  }

  return (
    <>
      {target.log_truncated && (
        <p className={styles.logNotice}>
          저장 상한에 도달해 로그 일부만 표시합니다
        </p>
      )}
      <pre className={styles.logOutput}>{target.restart_log}</pre>
    </>
  )
}

function TargetResult({ target }: { target: GuardianAlphaRestartResultTarget }) {
  const previousPidValues = target.previous_pids ?? null
  const previousPids = previousPidValues === null
    ? '수집하지 못했습니다'
    : previousPidValues.length > 0
      ? previousPidValues.join(', ')
      : '확인된 이전 프로세스 없음'

  return (
    <section className={styles.targetCard} aria-labelledby={`target-${target.guardian_alpha_node_id}`}>
      <div className={styles.targetHeader}>
        <h2 id={`target-${target.guardian_alpha_node_id}`} className={styles.targetTitle}>
          {target.logical_node_key}
        </h2>
        <span className={target.ok ? styles.targetSucceeded : styles.targetFailed}>
          {target.ok ? '확인 성공' : '확인 실패'}
        </span>
      </div>

      <dl className={styles.targetDetails}>
        <div>
          <dt>GuardianAlpha node ID</dt>
          <dd className={styles.mono}>{target.guardian_alpha_node_id}</dd>
        </div>
        <div>
          <dt>이전 PID</dt>
          <dd className={styles.mono}>{previousPids}</dd>
        </div>
        <div>
          <dt>재시작 후 확인 PID</dt>
          <dd className={styles.mono}>
            {target.matched_pids.length > 0 ? target.matched_pids.join(', ') : '—'}
          </dd>
        </div>
      </dl>

      <div className={styles.logSection}>
        <h3>재시작 로그</h3>
        <RestartLog target={target} />
      </div>
    </section>
  )
}

export default function GuardianAlphaRestartResultPage() {
  const { nodeId = '' } = useParams<{ nodeId: string }>()
  const [viewState, setViewState] = useState<ResultViewState>({
    nodeId,
    result: null,
    loading: true,
    error: null,
  })
  const requestSequence = useRef(0)
  const currentView = viewState.nodeId === nodeId
    ? viewState
    : { nodeId, result: null, loading: true, error: null }
  const { result, loading, error } = currentView

  const loadResult = useCallback(async (initial = false) => {
    const sequence = ++requestSequence.current
    if (initial) {
      setViewState({ nodeId, result: null, loading: true, error: null })
    }

    if (!nodeId) {
      setViewState({
        nodeId,
        result: null,
        loading: false,
        error: 'Node ID가 없습니다.',
      })
      return
    }

    try {
      const loaded = await getLatestGuardianAlphaRestartResult(nodeId)
      if (sequence !== requestSequence.current) return
      setViewState({
        nodeId,
        result: loaded,
        loading: false,
        error: null,
      })
    } catch (caught: unknown) {
      if (sequence !== requestSequence.current) return
      setViewState((current) => ({
        nodeId,
        result: current.nodeId === nodeId ? current.result : null,
        loading: false,
        error: errorMessage(caught),
      }))
    }
  }, [nodeId])

  useEffect(() => {
    void loadResult(true)
    return () => {
      requestSequence.current += 1
    }
  }, [loadResult])

  const active = result !== null && ACTIVE_STATUSES.has(result.status)

  useEffect(() => {
    if (!active) return
    let cancelled = false
    let timer: number | null = null

    const poll = async () => {
      await loadResult()
      if (cancelled) return
      timer = window.setTimeout(() => {
        void poll()
      }, POLL_INTERVAL_MS)
    }

    timer = window.setTimeout(() => {
      void poll()
    }, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [active, loadResult])

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <Link className={styles.backLink} to={nodeDetailPath(nodeId)}>
          ← Node Detail
        </Link>
        <div>
          <h1>GuardianAlpha 최근 재시작 결과</h1>
          <p className={styles.subtitle}>Node: {nodeId}</p>
        </div>
      </header>

      {loading && <div className={styles.pageMessage}>불러오는 중...</div>}

      {!loading && error && (
        <div className={styles.errorBox} role="alert">
          <div>{error}</div>
          {result === null && (
            <button
              type="button"
              className={styles.retryButton}
              onClick={() => void loadResult(true)}
            >
              다시 시도
            </button>
          )}
        </div>
      )}

      {!loading && !error && result === null && (
        <div className={styles.pageMessage}>재시작 결과 없음</div>
      )}

      {!loading && result !== null && (
        <>
          <section className={styles.commandCard} aria-labelledby="command-summary-title">
            <div className={styles.commandHeader}>
              <h2 id="command-summary-title">Command</h2>
              <JobStatusBadge status={result.status} />
            </div>

            {active && <div className={styles.runningNotice}>실행 중</div>}

            <dl className={styles.commandDetails}>
              <div>
                <dt>Command ID</dt>
                <dd className={styles.mono}>{result.command_id}</dd>
              </div>
              <div>
                <dt>요청 시각</dt>
                <dd>{formatKstDateTime(result.requested_at, '—')}</dd>
              </div>
              <div>
                <dt>완료 시각</dt>
                <dd>{formatKstDateTime(result.completed_at, '—')}</dd>
              </div>
            </dl>

            {result.error_message && (
              <div className={styles.commandError}>
                <strong>Command 오류</strong>
                <span>{result.error_message}</span>
              </div>
            )}
          </section>

          {result.targets.length > 0 ? (
            <div className={styles.targetList}>
              {result.targets.map((target) => (
                <TargetResult
                  key={target.guardian_alpha_node_id}
                  target={target}
                />
              ))}
            </div>
          ) : (
            <div className={styles.pageMessage}>
              {active ? '대상 결과를 기다리는 중입니다' : '대상별 결과가 없습니다'}
            </div>
          )}

          <p className={styles.footnote}>
            PID는 등록된 프로세스 조건과 5초 유지 확인 결과이며, 서비스 정상 또는 추론 준비 완료를 의미하지 않습니다.
          </p>
        </>
      )}
    </div>
  )
}
